import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { PROCESS_STATUS, ProcessListFilter } from '../../../../model/process-list-filter';
import { ProcessesDataService } from '../processes-data-service';
import { Person, Site, SiteEntry, SitePaging } from '@alfresco/js-api';
import { SitesService } from '@alfresco/adf-content-services';
import { map, takeUntil } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, Subscription } from 'rxjs';
import { PlanService } from '../../../form/services/plan.service';
import { Store } from '@ngrx/store';
import { getUserProfile } from '@alfresco/aca-shared/store';
import { LegacyApiService } from '../../../../api/services/legacy-api.service';
import { LegacyContentService } from '../../../../services/legacy-content.service';
import { DynamiclistsModel } from '../../../../api/models/epm/dynamiclists.model';
import { PaginationModel } from '@alfresco/adf-core';
import { createInstanceQuery } from '../../../../api/models/process/process-instance-query';

/** Process definitions (BPMN) which are handled by this ADF application
 * Name translation can be obtained using: this.translation.instant(`EPM.PROCESS.LABEL.${id.toUpperCase()}`)
 * */
export const PROCESS_DEFINITIONS_IDS = ['stdWorkflow', 'stdWorkflow2', 'issueWorkflow'];

@Component({
  selector: 'epm-process-filters',
  templateUrl: './processes-filter.component.html',
  styleUrls: ['./processes-filter.component.scss']
})
export class ProcessesFilterComponent implements OnInit, OnDestroy {
  @Output() filtersVisible: EventEmitter<boolean> = new EventEmitter();

  /** Expand  processes filter component */
  isExpanded = true;

  /** Filters form */
  filtersForm = this.fb.group({
    siteId: new FormControl('', []),
    definitionId: new FormControl('issueWorkflow', [Validators.required]),
    startedAfter: new FormControl<Date | null>(null),
    startedBefore: new FormControl<Date | null>(null),
    startedBy: new FormControl(''),
    assignedTo: new FormControl(''),
    assignee: new FormControl(''),
    priority: new FormControl<number>(null, [Validators.min(1), Validators.max(3)]),
    plan: new FormControl(''),
    types: new FormArray([new FormControl('any')]),
    handover: new FormControl(''),
    status: new FormControl(PROCESS_STATUS.ACTIVE, Validators.required)
  });
  isStartedByOpen = false;
  isAssigneeSearchOpen = false;
  isSmallScreen = false;

  /** Fetched sites to populate site filter */
  sites: Site[];
  readonly processDefinitionsIds = PROCESS_DEFINITIONS_IDS;
  private sitesSub: Subscription;
  private readonly onDestroy$ = new Subject<boolean>();

  // Plan-aligned data sources
  plans: any[] = [];
  types: { name: string; ref: string }[] = [];
  handovers: string[] = [];
  dynamicLists: DynamiclistsModel;
  private isRestoringState = false;

  constructor(
    private fb: FormBuilder,
    private processesDataService: ProcessesDataService,
    private sitesService: SitesService,
    private breakpointObserver: BreakpointObserver,
    private planService: PlanService,
    private store: Store,
    private legacyApiService: LegacyApiService,
    private legacyContentService: LegacyContentService
  ) {}

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape]).subscribe((result) => {
      this.isSmallScreen = result.matches;
    });
    this._fetchSites();
    this.store
      .select(getUserProfile)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((profile) => {
        if (profile.id) {
          this.filtersForm.controls['startedBy'].setValue(profile.id);
        }
      });
    this.filtersVisible.emit(true);
    this.planService.setSelectedPlan(false);

    // Load supplemental data for plan-aligned filters and restore state
    this.isRestoringState = !!this.processesDataService.filterData;
    this._initTypesAndHandovers();

    // React on site change to load plans, filter types by site, and reset selections/handovers
    this.filterSiteId.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((siteId: string) => {
      if (siteId) {
        this._fetchPlans(siteId);
      } else {
        this.plans = [];
      }
      // Filter available types by site
      if (this.dynamicLists?.data) {
        this.types = this._filterProcessTypesBySiteId(this.dynamicLists).map((type) => ({ name: type.name, ref: type.ref }));
      } else {
        this.types = [];
      }
      // Only reset types and handovers if not restoring state (to preserve restored values)
      if (!this.isRestoringState) {
        this.typesFormArray.clear();
        this.typesFormArray.push(new FormControl('any'));
        // Reset handovers when site changes
        this.handovers = [];
        this.filterHandover.setValue('');
      }
    });

    // React on types change to compute handovers list (union if none/any, intersection otherwise)
    this.typesFormArray.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((values: string[]) => {
      const lastType = values[values.length - 1];
      if (lastType && lastType !== 'any' && this.typesFormArray.length < this.types.length + 1) {
        this.typesFormArray.push(new FormControl(''));
      }

      const selectedRefs = values.filter((type: string) => !!type && type !== 'any');
      const includeAny = values.includes('any');
      const allowedRefsForSite = this._getAllowedTypeRefsForCurrentSite();
      const refsToLoad = includeAny ? allowedRefsForSite : selectedRefs;

      if (refsToLoad.length === 0) {
        this.handovers = [];
        this.filterHandover.setValue('');
        return;
      }

      Promise.all(refsToLoad.map((ref) => this.legacyApiService.retrieveUxData(ref))).then((uxDataArray) => {
        const handoverArrays = uxDataArray.map((ux) => ux.handoverOptions || []);
        const result = includeAny
          ? Array.from(new Set(handoverArrays.flat()))
          : ((): string[] => {
              const handoverSets = handoverArrays.map((arr) => new Set(arr));
              return handoverSets.length ? [...handoverSets[0]].filter((opt) => handoverSets.every((s) => s.has(opt))) : [];
            })();
        this.handovers = result;
        const cur = this.filterHandover.value;
        if (cur && !result.includes(cur)) {
          this.filterHandover.setValue('');
        }
      });
    });

    this.onFiltersFormSubmit();
  }

  onFiltersFormSubmit() {
    // when form is submitted
    if (this.filtersForm.valid) {
      const filtersObj = this.createProcessQueryRequestData();
      this.processesDataService.filters$.next(filtersObj);
      this.isExpanded = false;
      this.filtersVisible.emit(false);
    }
  }

  private createProcessQueryRequestData(): ProcessListFilter {
    const filtersObj = new ProcessListFilter(this.filtersForm.value);

    // Build type names string from selected refs
    const typesValue = this.typesFormArray.value as string[];
    const selectedRefs = typesValue.filter((t: string) => t && t !== 'any');
    const selectedNames = selectedRefs.map((ref) => this.dynamicLists?.data?.find((dl) => dl.ref === ref)?.name).filter((n): n is string => !!n);
    const typeValue = selectedNames.length === 0 ? 'any' : selectedNames.join('|');
    if (typeValue !== 'any') {
      filtersObj.type = typeValue;
      filtersObj.types = selectedNames;
    }

    return filtersObj;
  }

  toggleStartedBySelect() {
    // hides/shows startedBy people search
    this.isStartedByOpen = !this.isStartedByOpen;
  }

  onStartedBySelect(person: Person) {
    // executed when startedBy user is selected
    this.filtersForm.patchValue({ startedBy: person.id });
    this.isStartedByOpen = false;
  }

  onAssigneeSelect(person: Person) {
    this.filtersForm.patchValue({ assignee: person.id });
    this.isAssigneeSearchOpen = false;
  }

  onReset() {
    this.filtersForm.reset({
      siteId: '',
      status: PROCESS_STATUS.ACTIVE,
      definitionId: 'issueWorkflow',
      types: ['any'],
      handover: ''
    });
    this.typesFormArray.clear();
    this.typesFormArray.push(new FormControl('any'));
  }

  onExpandedChange(expanded: boolean) {
    this.filtersVisible.emit(expanded);
  }

  ngOnDestroy() {
    this.processesDataService.filterData = this.createProcessQueryRequestData();
    this.sitesSub?.unsubscribe();
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  private _fetchSites() {
    this.sitesSub = this.sitesService
      .getSites()
      .pipe(map((sitePaging: SitePaging) => sitePaging.list.entries.map((siteEntry: SiteEntry) => siteEntry.entry)))
      .subscribe((sites: Site[]) => (this.sites = sites));
  }

  getDateFormatString() {
    return 'DD/MM/YYYY';
  }

  get filterSiteId(): AbstractControl {
    return this.filtersForm.get('siteId');
  }

  get filterPlan(): AbstractControl {
    return this.filtersForm.get('plan');
  }

  get filterHandover(): AbstractControl {
    return this.filtersForm.get('handover');
  }

  get typesFormArray(): FormArray {
    return this.filtersForm.get('types') as FormArray;
  }

  getAvailableTypes(index: number): { name: string; ref: string }[] {
    const selectedTypes = this.typesFormArray.value.filter((type: string, i: number) => type && type !== 'any' && i !== index);
    return this.types.filter((type) => !selectedTypes.includes(type.ref));
  }

  removeTypeField(index: number): void {
    if (this.typesFormArray.length > 1) {
      this.typesFormArray.removeAt(index);
    }
  }

  getTypeControl(index: number): FormControl {
    return this.typesFormArray.at(index) as FormControl;
  }

  private _fetchPlans(siteid: string) {
    // Reuse legacy API used in plan filter
    if (siteid) {
      this.legacyContentService.getIssuePlans(siteid).subscribe((plans) => {
        this.plans = plans.map((plan) => ({ ...plan, name: this._removeFileExtension(plan.name) }));
      });
    }
  }

  private _initTypesAndHandovers() {
    this.legacyApiService.fetchWfTypes('epmIssueWf:issueWorkflowDataListItem').then((dynamicList) => {
      this.dynamicLists = dynamicList;

      // Restore state after types are loaded
      if (this.processesDataService.filterData) {
        const filterData = this.processesDataService.filterData;
        // Restore form values except types (which will be handled separately)
        const { types: savedTypes, ...restFilterData } = filterData;
        this.filtersForm.patchValue(restFilterData);

        // Load plans if siteId is set
        if (restFilterData.siteId) {
          this._fetchPlans(restFilterData.siteId);
        }

        // Filter types by site after restoring siteId
        this.types = this._filterProcessTypesBySiteId(dynamicList).map((type) => ({ name: type.name, ref: type.ref }));

        // Restore types FormArray if types exist
        if (savedTypes && Array.isArray(savedTypes) && savedTypes.length > 0) {
          // Map type names back to refs
          const typeRefs = savedTypes
            .map((typeName) => this.dynamicLists.data.find((dl) => dl.name === typeName)?.ref)
            .filter((ref): ref is string => !!ref);

          if (typeRefs.length > 0) {
            this.typesFormArray.clear();
            typeRefs.forEach((ref) => this.typesFormArray.push(new FormControl(ref)));
            // If 'any' was selected, add it as first option
            if (savedTypes.includes('any') || savedTypes.length === 0) {
              this.typesFormArray.insert(0, new FormControl('any'));
            }
          } else {
            this.typesFormArray.setValue(['any']);
          }
        } else {
          this.typesFormArray.setValue(['any']);
        }
        // Clear restoration flag after state is restored
        this.isRestoringState = false;
      } else {
        this.types = this._filterProcessTypesBySiteId(dynamicList).map((type) => ({ name: type.name, ref: type.ref }));
        this.typesFormArray.setValue(['any']);
      }
    });
  }

  private _removeFileExtension(fileName: string): string {
    return fileName ? fileName.replace(/\.[^/.]+$/, '') : fileName;
  }

  private _filterProcessTypesBySiteId(processTypes: DynamiclistsModel) {
    const siteId = this.filterSiteId.value;
    if (siteId) {
      return processTypes.data.filter((type) => type.siteid === siteId);
    }
    // No site selected: do not expose any types
    return [];
  }

  private _getAllowedTypeRefsForCurrentSite(): string[] {
    if (!this.dynamicLists?.data) {
      return [];
    }
    const siteId = this.filterSiteId.value;
    const list = siteId ? this.dynamicLists.data.filter((t) => t.siteid === siteId) : this.dynamicLists.data;
    return list.map((t) => t.ref);
  }

  async onGenerateCsv() {
    await this.triggerLegacyReportDownload('csv');
  }

  async onGeneratePdf() {
    await this.triggerLegacyReportDownload('pdf');
  }

  /**
   * Calls new ProcessesImpl-based download endpoint (via LegacyApiService)
   * using current form values & pagination and triggers browser download.
   * This uses the same filtering as the Angular UI list for consistency.
   */
  private async triggerLegacyReportDownload(format: 'csv' | 'pdf'): Promise<void> {
    const queryParams = this.buildProcessReportQueryParams(format);
    if (!queryParams) {
      return;
    }

    const blob = await this.legacyApiService.downloadProcessesReport(queryParams);
    if (!blob) {
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `raport_${new Date()
      .toISOString()
      .replace(/[-:T.]/g, '_')
      .slice(0, 19)}.${format}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }

  /**
   * Build query parameters for new ProcessesImpl-based report endpoint (PDF/CSV),
   * using current form values directly (not requiring Filter button click).
   * This uses the same filtering as the Angular UI list (ProcessesImpl).
   */
  private buildProcessReportQueryParams(format: 'csv' | 'pdf'): string | null {
    // Build filters from current form values (same as createProcessQueryRequestData)
    const filters = this.createProcessQueryRequestData();
    const pagination: PaginationModel = this.processesDataService.searchedPagination;

    // Use the same query building logic as the Angular UI list
    const page = pagination ? Math.floor((pagination.skipCount || 0) / (pagination.maxItems || 50)) : 0;
    const size = pagination?.maxItems || 50;
    const sort: string[] = ['startedAt', 'desc']; // Default sort

    const processInstanceQuery = createInstanceQuery(page, size, sort, filters);

    const params = new URLSearchParams();

    // Pass where clause (same format as ProcessesImpl expects)
    if (processInstanceQuery.where) {
      params.append('where', processInstanceQuery.where);
    }

    // Pagination
    if (processInstanceQuery.skipCount !== undefined) {
      params.append('skipCount', String(processInstanceQuery.skipCount));
    }
    if (processInstanceQuery.maxItems !== undefined) {
      params.append('maxItems', String(processInstanceQuery.maxItems));
    }

    // Format
    if (format === 'csv') {
      params.append('format', 'csv');
    }

    return params.toString();
  }
}
